import { cn } from '@/lib/cn'

/* Base UI (`@base-ui/react`): no primitive. A tag is a static label; the one interactive variant
   here is a plain button with no popup, menu or toggle semantics to inherit. */

/** A small square-cornered label for a type or category — participant role,
 *  document kind, transport mode. Distinct from StatusPill by shape: pills are
 *  fully round and mean lifecycle state, tags are `rounded-sm` and mean kind. */
export function Tag({
  children,
  className,
  tone = 'neutral',
}: {
  children: React.ReactNode
  className?: string
  /** `positive` is a cost line's «Оплачено» (S-4.2): a word beside a name
   *  that says a state, where a full pill would out-shout the name it sits
   *  next to. Never `brand` for a finished state — blue is in-progress only. */
  tone?: 'neutral' | 'brand' | 'attention' | 'positive'
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
        tone === 'brand'
          ? 'bg-brand-soft text-brand'
          : tone === 'attention'
            ? 'bg-attention-soft text-attention'
            : tone === 'positive'
              ? 'bg-positive-soft text-positive'
              : 'bg-neutral-100 text-text-muted',
        className,
      )}
    >
      {children}
    </span>
  )
}

/** The FR-17 rate memory chip: the remembered amount together with its source
 *  case and month, so its authority can be judged. It REPORTS; it never
 *  silently fills a field. Accepting is one click, ignoring costs nothing. */
export function RateMemoryChip({
  amount,
  sourceCaseNumber,
  month,
  onAccept,
}: {
  amount: string
  sourceCaseNumber: string
  month: string
  onAccept?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onAccept}
      title={`Use ${amount} from ${sourceCaseNumber}`}
      className={cn(
        'bg-brand-soft text-brand inline-flex items-center gap-1.5 rounded-sm',
        'cursor-pointer border-0 px-1.5 py-0.5 text-[11px] whitespace-nowrap',
        'hover:bg-brand-border transition-colors duration-[var(--dur-fast)]',
      )}
    >
      <span className="asa-num font-semibold">{amount}</span>
      <span className="opacity-70">
        {sourceCaseNumber} · {month}
      </span>
    </button>
  )
}
